import {
  BarChart3,
  Crosshair,
  Droplets,
  Landmark,
  LayoutDashboard,
  Receipt,
  ShieldX,
  Sparkles,
  UserCheck,
  Users,
  Zap,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { paths } from 'src/config/paths';
import { Product } from 'src/modules/products/domain';
import type { Project } from 'src/modules/projects/domain';

// Only the contract address is read here — narrowing the parameter keeps callers
// (and tests) from having to build or fake an entire Project.
type ProjectNavContext = Pick<Project, 'contractAddress'>;

export interface Item {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  items?: Item[];
  label?: ReactNode;
  path?: string;
  title: string;
  tooltip?: string;
  showTooltip?: boolean;
}

export interface Section {
  items: Item[];
  subheader?: string;
}

const dashboard = (slug: string): Item => ({
  title: 'Dashboard',
  path: paths.project.overview(slug),
  icon: <LayoutDashboard size={16} />,
});

// Segments is one project-level list seen from every product, but Badges only
// surfaces where users earn them — Affiliates never shows it.
const segments = (slug: string, withBadges: boolean): Item => ({
  title: 'Segments',
  path: paths.project.affiliate.audiences.overview(slug),
  icon: <Crosshair size={16} />,
  items: [
    { title: 'Tiers', path: paths.project.affiliate.tiers(slug) },
    { title: 'Audiences', path: paths.project.affiliate.audiences.overview(slug) },
    ...(withBadges ? [{ title: 'Badges', path: paths.project.affiliate.badges(slug) }] : []),
    { title: 'Blacklist', path: paths.project.affiliate.blacklist(slug) },
  ],
});

const allUsers = (slug: string): Item => ({
  title: 'All users',
  path: paths.project.participants.users(slug),
  icon: <Users size={16} />,
});

const budgets = (slug: string, project?: ProjectNavContext): Item => ({
  title: 'Budgets',
  path: paths.project.affiliate.budgets(slug),
  disabled: !project?.contractAddress,
  icon: <Landmark size={16} />,
  tooltip: 'There is no incentives program created and budgets are not initialized yet',
  showTooltip: !project?.contractAddress,
});

const rewardPayouts = (slug: string): Item => ({
  title: 'Reward payouts',
  path: paths.project.payouts.rewardPayouts(slug),
  icon: <Receipt size={16} />,
});

const customDashboard = (slug: string): Item => ({
  title: 'Custom dashboard',
  path: paths.project.charts(slug),
  icon: <BarChart3 size={16} />,
});

const fraudDetection = (slug: string): Item => ({
  title: 'Fraud detection',
  path: paths.project.fraud.home(slug),
  icon: <ShieldX size={16} />,
});

const activity = (slug: string): Item => ({
  title: 'Activity',
  path: paths.project.activity.home(slug),
  icon: <Zap size={16} />,
  items: [
    { title: 'Conversions', path: paths.project.activity.conversions(slug) },
    { title: 'Events', path: paths.project.activity.events(slug) },
  ],
});

const affiliatesSections = (slug: string, project?: ProjectNavContext): Section[] => [
  {
    subheader: 'Overview',
    items: [dashboard(slug)],
  },
  {
    subheader: 'Affiliates',
    items: [
      {
        title: 'All affiliates',
        path: paths.project.affiliateManagement.dashboard(slug),
        icon: <UserCheck size={16} />,
      },
      {
        title: 'Master affiliates',
        path: paths.project.affiliateManagement.masters(slug),
        icon: <Users size={16} />,
      },
      {
        title: 'Applications',
        path: paths.project.affiliateManagement.applications(slug),
        icon: <UserCheck size={16} />,
      },
      {
        title: 'Referral codes',
        path: paths.project.affiliateManagement.referralCodes(slug),
        icon: <Sparkles size={16} />,
      },
      segments(slug, false),
    ],
  },
  {
    subheader: 'Payouts',
    items: [
      budgets(slug, project),
      {
        title: 'Pending approval',
        path: paths.project.payouts.pendingApproval(slug),
        icon: <Receipt size={16} />,
      },
      rewardPayouts(slug),
      {
        title: 'Invoices',
        path: paths.project.payouts.invoices(slug),
        icon: <Receipt size={16} />,
      },
    ],
  },
  {
    subheader: 'Insights',
    items: [
      {
        title: 'Breakdown',
        path: paths.project.affiliateManagement.breakdown(slug),
        icon: <BarChart3 size={16} />,
      },
      customDashboard(slug),
      fraudDetection(slug),
      activity(slug),
    ],
  },
];

const incentivesSections = (slug: string, project?: ProjectNavContext): Section[] => [
  {
    subheader: 'Overview',
    items: [dashboard(slug)],
  },
  {
    subheader: 'Users',
    items: [allUsers(slug), segments(slug, true)],
  },
  {
    subheader: 'Payouts',
    items: [
      budgets(slug, project),
      rewardPayouts(slug),
      {
        title: 'Pool payouts',
        path: paths.project.payouts.poolPayouts(slug),
        icon: <Receipt size={16} />,
      },
    ],
  },
  {
    subheader: 'Insights',
    items: [customDashboard(slug), fraudDetection(slug), activity(slug)],
  },
];

// Points has no Payouts group — points are credited automatically, there is
// nothing to approve or claim.
const pointsSections = (slug: string): Section[] => [
  {
    subheader: 'Overview',
    items: [
      dashboard(slug),
      {
        title: 'Points airdrop',
        path: paths.project.airdrops.points(slug),
        icon: <Droplets size={16} />,
      },
    ],
  },
  {
    subheader: 'Users',
    items: [allUsers(slug), segments(slug, true)],
  },
  {
    subheader: 'Insights',
    items: [
      {
        title: 'Points analytics',
        path: paths.project.points.overview(slug),
        icon: <BarChart3 size={16} />,
      },
      customDashboard(slug),
      fraudDetection(slug),
      activity(slug),
    ],
  },
];

const productSections: Record<Product, (slug: string, project?: ProjectNavContext) => Section[]> = {
  affiliates: affiliatesSections,
  incentives: incentivesSections,
  points: pointsSections,
};

export const buildProjectSections = (slug: string, product: Product, project?: ProjectNavContext): Section[] =>
  productSections[product](slug, project);
